import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Star, Send, MapPin, Loader2, Check, AlertCircle, Copy, ExternalLink, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// AI Review Generation - Creates unique reviews to avoid Google spam detection
const POSITIVE_ADJECTIVES = ["amazing", "fantastic", "excellent", "top-notch", "superb", "outstanding", "wonderful", "exceptional", "incredible", "remarkable"];
const SERVICE_KEYWORDS = ["service", "experience", "team", "support", "staff", "quality", "care", "attention"];
const POSITIVE_PHRASES = [
  "Highly recommend to everyone!",
  "Will definitely come back!",
  "Best decision I made!",
  "Exceeded all my expectations!",
  "Truly impressed!",
  "A must-visit place!",
  "5 stars well deserved!",
  "Couldn't be happier!"
];

// Quick Tags for one-tap review generation
const QUICK_TAGS = [
  { id: 'service', label: '👍 Great Service', keywords: ['service', 'professional', 'helpful'] },
  { id: 'staff', label: '😊 Friendly Staff', keywords: ['staff', 'team', 'friendly', 'welcoming'] },
  { id: 'value', label: '💰 Value for Money', keywords: ['value', 'worth', 'price', 'quality'] },
  { id: 'quality', label: '⭐ Excellent Quality', keywords: ['quality', 'premium', 'top-notch'] },
  { id: 'recommend', label: '🎯 Highly Recommend', keywords: ['recommend', 'best', 'amazing'] },
];

const PublicReview = () => {
  const { qrCode } = useParams();
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [redirectToGoogle, setRedirectToGoogle] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [generatedReview, setGeneratedReview] = useState('');
  const [reviewCopied, setReviewCopied] = useState(false);
  const [showRedirectScreen, setShowRedirectScreen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    issue_category: '',
    feedback_text: '',
    photo_urls: []
  });

  useEffect(() => {
    fetchReviewData();
  }, [qrCode]);

  // Generate unique review based on selected tags
  useEffect(() => {
    if (selectedTags.length > 0) {
      generateAIReview();
    }
  }, [selectedTags, reviewData]);

  const fetchReviewData = async () => {
    try {
      const res = await api.get(`/magic-qr/review/${qrCode}`);
      setReviewData(res.data);
    } catch (error) {
      console.error('Failed to fetch review data:', error);
    } finally {
      setLoading(false);
    }
  };

  // AI Review Generator - Creates unique reviews to avoid Google spam filter
  const generateAIReview = () => {
    const adj = POSITIVE_ADJECTIVES[Math.floor(Math.random() * POSITIVE_ADJECTIVES.length)];
    const service = SERVICE_KEYWORDS[Math.floor(Math.random() * SERVICE_KEYWORDS.length)];
    const phrase = POSITIVE_PHRASES[Math.floor(Math.random() * POSITIVE_PHRASES.length)];
    
    // Build review based on selected tags
    let tagText = '';
    if (selectedTags.includes('service')) tagText += 'The service was impeccable. ';
    if (selectedTags.includes('staff')) tagText += 'The staff was incredibly friendly and helpful. ';
    if (selectedTags.includes('value')) tagText += 'Great value for money. ';
    if (selectedTags.includes('quality')) tagText += 'The quality exceeded my expectations. ';
    if (selectedTags.includes('recommend')) tagText += 'I would recommend this to everyone. ';
    
    const businessName = reviewData?.business_name || 'this business';
    const review = `Had a ${adj} ${service} at ${businessName}! ${tagText}${phrase}`;
    
    setGeneratedReview(review);
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
    if (newRating < 4) {
      setShowFeedbackForm(true);
    } else {
      setShowFeedbackForm(false);
    }
  };

  // Copy review to clipboard and show redirect screen
  const handleCopyAndRedirect = async () => {
    if (!generatedReview && selectedTags.length === 0) {
      // Generate a default review if no tags selected
      const adj = POSITIVE_ADJECTIVES[Math.floor(Math.random() * POSITIVE_ADJECTIVES.length)];
      const phrase = POSITIVE_PHRASES[Math.floor(Math.random() * POSITIVE_PHRASES.length)];
      const defaultReview = `Had a ${adj} experience at ${reviewData?.business_name}! ${phrase}`;
      setGeneratedReview(defaultReview);
      
      try {
        await navigator.clipboard.writeText(defaultReview);
      } catch (err) {
        console.error('Clipboard error:', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(generatedReview);
      } catch (err) {
        console.error('Clipboard error:', err);
      }
    }
    
    setReviewCopied(true);
    toast.success('✅ Review Copied to Clipboard!', {
      description: 'Paste it on Google Reviews',
      duration: 3000
    });
    
    // Record the submission
    setSubmitting(true);
    try {
      await api.post(`/magic-qr/review/${qrCode}/submit`, {
        qr_id: qrCode,
        rating: 5,
        name: 'Happy Customer',
        phone: '',
        feedback_text: generatedReview || 'Positive review submitted'
      });
    } catch (error) {
      console.error('Failed to record:', error);
    }
    setSubmitting(false);
    
    // Show the redirect screen with human interaction delay
    setShowRedirectScreen(true);
  };

  // Handle the "Go to Google Maps" button click (human interaction requirement)
  const handleGoToGoogle = () => {
    // Mark as submitted so user can come back to thank-you screen.
    // Note: anchor's target="_blank" handles the actual navigation reliably,
    // so we don't call window.location.href here (which can be blocked on mobile).
    setTimeout(() => setSubmitted(true), 1500);
  };

  const handleSubmitFeedback = async () => {
    if (!formData.name || !formData.phone || !formData.feedback_text) {
      toast.error('Please fill in your name, phone, and feedback');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/magic-qr/review/${qrCode}/submit`, {
        qr_id: qrCode,
        rating,
        ...formData
      });
      
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!reviewData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 p-6">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">QR Code Not Found</h1>
          <p className="text-muted-foreground">This review link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  // STEP 3: Redirect Screen with Human Interaction (Anti-spam)
  if (showRedirectScreen && !submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#0a0a0f' }}>
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center animate-bounce" style={{ backgroundColor: reviewData.theme_color + '20' }}>
            <Check className="w-10 h-10" style={{ color: reviewData.theme_color }} />
          </div>
          
          <h1 className="text-3xl font-bold mb-4">Review Copied! ✅</h1>
          <p className="text-lg text-muted-foreground mb-6">
            Your review is ready to paste on Google
          </p>
          
          {/* Show the copied review */}
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left border border-white/10">
            <p className="text-sm text-muted-foreground mb-2">📝 Your Review:</p>
            <p className="italic text-sm">"{generatedReview || reviewData.praise_quote}"</p>
          </div>
          
          {/* Instructions */}
          <div className="bg-green-500/10 rounded-xl p-4 mb-6 text-left border border-green-500/20">
            <p className="font-semibold text-green-500 mb-2">📋 Google पर Review कैसे करें:</p>
            <ol className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">1</span>
                <span>नीचे button दबाएं (Google खुलेगा)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">2</span>
                <span>Google पर <strong>5 Stars ⭐⭐⭐⭐⭐</strong> select करें</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">3</span>
                <span>Review box में <strong>PASTE</strong> करें (Ctrl+V या long press)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-green-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">4</span>
                <span><strong>POST</strong> button दबाएं!</span>
              </li>
            </ol>
          </div>
          
          {/* Human interaction button - Required for spam prevention */}
          <a
            href={reviewData.google_review_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleGoToGoogle}
            className="w-full py-4 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-3 shadow-lg hover:scale-105 transition-transform animate-pulse"
            style={{ backgroundColor: '#4285F4', textDecoration: 'none' }}
            data-testid="go-to-google-btn"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google पर Review करें (नया Tab खुलेगा)
          </a>
          
          {/* Alternative link if button doesn't work */}
          <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
            <p className="text-xs text-yellow-500 mb-2">अगर button काम नहीं करे तो:</p>
            <a 
              href={reviewData.google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-400 underline break-all"
            >
              👉 यहाँ Click करें (Google Maps खुलेगा)
            </a>
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Your review helps {reviewData.business_name} grow! 🙏
          </p>
        </div>
      </div>
    );
  }

  // Final Thank You screen
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#0a0a0f' }}>
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: reviewData.theme_color + '20' }}>
            <Check className="w-10 h-10" style={{ color: reviewData.theme_color }} />
          </div>
          
          {rating >= 4 ? (
            <>
              <h1 className="text-3xl font-bold mb-4">Thank You! ⭐</h1>
              <p className="text-lg text-muted-foreground mb-6">
                We're thrilled you had a great experience!
              </p>
              <p className="text-sm text-muted-foreground">
                Your support means the world to us.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold mb-4">Thank You for Your Feedback</h1>
              <p className="text-lg text-muted-foreground mb-6">
                We appreciate you taking the time to share your experience. Your feedback helps us improve.
              </p>
              <p className="text-sm text-muted-foreground">
                The owner has been notified personally and will address your concerns.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#0a0a0f' }} data-testid="public-review">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {reviewData.logo_url && (
            <img 
              src={reviewData.logo_url} 
              alt={reviewData.business_name}
              className="w-20 h-20 mx-auto mb-4 rounded-full object-cover"
            />
          )}
          <h1 className="text-2xl font-bold mb-2">{reviewData.business_name}</h1>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{reviewData.business_location}</span>
          </div>
        </div>

        {/* Review Card */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold text-center mb-6">
            How was your experience?
          </h2>

          {/* Star Rating */}
          <div className="flex justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRatingChange(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 transition-transform hover:scale-110"
                data-testid={`star-${star}`}
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    star <= (hoverRating || rating)
                      ? 'fill-yellow-500 text-yellow-500'
                      : 'text-gray-600'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Rating Label */}
          <p className="text-center text-lg mb-6" style={{ color: reviewData.theme_color }}>
            {rating === 5 && '⭐ Excellent!'}
            {rating === 4 && '👍 Very Good!'}
            {rating === 3 && '😐 Good'}
            {rating === 2 && '😕 Fair'}
            {rating === 1 && '😞 Poor'}
          </p>

          {/* POSITIVE FLOW (4-5 Stars): Quick Tags + AI Review + Copy */}
          {rating >= 4 && !showFeedbackForm && (
            <div className="space-y-4">
              {/* Quick Tags - One-tap review helpers */}
              <div>
                <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  Tap to describe your experience:
                </p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_TAGS.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagToggle(tag.id)}
                      className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedTags.includes(tag.id)
                          ? 'bg-yellow-500 text-black'
                          : 'bg-white/10 hover:bg-white/20'
                      }`}
                      data-testid={`tag-${tag.id}`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Generated Review Preview */}
              {(generatedReview || selectedTags.length > 0) && (
                <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-green-500" />
                    <p className="text-sm font-medium text-green-500">Your AI-Generated Review:</p>
                  </div>
                  <p className="italic text-sm">"{generatedReview}"</p>
                </div>
              )}

              {/* Default praise quote if no tags selected */}
              {selectedTags.length === 0 && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <p className="text-sm text-muted-foreground mb-2">Or use our suggestion:</p>
                  <p className="italic text-sm">"{reviewData.praise_quote}"</p>
                </div>
              )}
              
              {/* Copy & Go to Google Button */}
              <button
                onClick={handleCopyAndRedirect}
                disabled={submitting}
                className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{ backgroundColor: reviewData.theme_color }}
                data-testid="copy-and-redirect-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : reviewCopied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied! Continue to Google
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy Review & Post on Google
                  </>
                )}
              </button>
              
              <p className="text-xs text-center text-muted-foreground">
                Review will be copied to clipboard, then you'll be redirected to Google
              </p>
            </div>
          )}

          {/* NEGATIVE FLOW (1-3 Stars): Internal Feedback Form */}
          {(rating < 4 || showFeedbackForm) && (
            <div className="space-y-4">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                <p className="text-sm text-yellow-500 font-medium">
                  😔 We're sorry to hear that!
                </p>
                <p className="text-sm text-yellow-400/80 mt-1">
                  Please tell us what went wrong so we can fix it. Your feedback stays private and goes directly to the manager.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Your Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Enter your name"
                  data-testid="feedback-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Enter your phone number"
                  data-testid="feedback-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email (Optional)</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What can we improve?</label>
                <select
                  value={formData.issue_category}
                  onChange={(e) => setFormData({ ...formData, issue_category: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  data-testid="feedback-category"
                >
                  <option value="">Select a category</option>
                  {reviewData.issue_categories?.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Your Feedback *</label>
                <textarea
                  value={formData.feedback_text}
                  onChange={(e) => setFormData({ ...formData, feedback_text: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
                  rows={4}
                  placeholder="Tell us about your experience..."
                  data-testid="feedback-text"
                />
              </div>

              <button
                onClick={handleSubmitFeedback}
                disabled={submitting}
                className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
                style={{ backgroundColor: reviewData.theme_color }}
                data-testid="submit-feedback-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback to Manager
                  </>
                )}
              </button>
              
              <p className="text-xs text-center text-muted-foreground">
                Your feedback is sent directly to the owner and stays private
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by ANANT Magic Tool
        </p>
      </div>
    </div>
  );
};

export default PublicReview;
